import classNames from "classnames";
import { get, isEmpty, isNil, keyBy, sortBy } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiChevronsLeft, BiChevronsRight, BiPlus } from "react-icons/bi";
import { NAME_SPACES } from "shared/locales/constants";
import CanvasIcon from "./CanvasIcon";
import { describeRules, isBackEdge, RULE_MODES } from "./next-steps";
import { ICON_PATHS, stepLook } from "./step-look";
import { describeStep, stepTone } from "./step-summary";
import { orderedStates, stateWhen } from "./workshop-view";

const COLLAPSED_KEY = "canvas.steps.collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch (_) {
    return false;
  }
};

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export const orderSteps = (nodes = []) =>
  sortBy(nodes, [
    (node) => Number(get(node, "data.order")) || 0,
    (node) => normalize(get(node, "data.name")),
  ]);

export const filterSteps = (steps = [], search = "") => {
  const query = normalize(search).trim();
  if (isEmpty(query)) return steps;
  return steps.filter((node) =>
    normalize(get(node, "data.name")).includes(query),
  );
};

const mainNextStep = (edges, id) => {
  const rules = describeRules(edges, id);
  const chosen =
    rules.find((rule) => rule.mode !== RULE_MODES.CONDITIONAL) ||
    rules[rules.length - 1];
  return get(chosen, "edge.target") || null;
};

export const railRows = (nodes = [], edges = []) => {
  const byId = keyBy(nodes, "id");
  const ordered = orderSteps(nodes);
  const placed = new Set();
  const mainLine = [];
  let current = get(ordered, "0.id");
  while (current && byId[current] && !placed.has(current)) {
    placed.add(current);
    mainLine.push(current);
    current = mainNextStep(edges, current);
  }
  const rows = [];
  mainLine.forEach((id, index) => {
    rows.push({ node: byId[id], number: index + 1, branch: false });
    const branches = edges
      .filter((edge) => edge.source === id && !isBackEdge(edge))
      .map((edge) => edge.target)
      .filter((target) => byId[target] && !placed.has(target));
    for (const target of branches) {
      if (!placed.has(target)) {
        placed.add(target);
        rows.push({ node: byId[target], number: null, branch: true });
      }
    }
  });
  for (const node of ordered)
    if (!placed.has(node.id)) rows.push({ node, number: null, branch: true });
  return rows;
};

export const startFrom = (nodes = [], edges = [], id) => {
  if (!nodes.some((node) => node.id === id)) return nodes;
  const lowest = Math.min(
    0,
    ...nodes.map((node) => Number(get(node, "data.order")) || 0),
  );
  const leading = nodes.map((node) =>
    node.id === id
      ? { ...node, data: { ...node.data, order: String(lowest - 1) } }
      : node,
  );
  const orderById = railRows(leading, edges).reduce(
    (result, row, index) => ({ ...result, [row.node.id]: String(index + 1) }),
    {},
  );
  return nodes.map((node) =>
    node.data.order === orderById[node.id]
      ? node
      : { ...node, data: { ...node.data, order: orderById[node.id] } },
  );
};

const StepList = ({
  nodes,
  edges,
  checksById,
  selectedId,
  selectedStateId,
  variables,
  onSelect,
  onSelectState,
  onOpenJourney,
  onAdd,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const STEPS = t("CANVAS_EDITOR.STEPS", { returnObjects: true });
  const STATES = t("SCREEN_WORKSHOP.STATES", { returnObjects: true }) || {};
  const CONDITIONS =
    t("FORM.POSSIBLE_STATUSES.CONDITIONS", { returnObjects: true }) || {};
  const [search, setSearch] = useState("");
  const [openStates, setOpenStates] = useState({});
  const rows = useMemo(() => railRows(nodes, edges), [nodes, edges]);
  const matching = new Set(filterSteps(nodes, search).map((node) => node.id));
  const visible = rows.filter((row) => matching.has(row.node.id));
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch (_) {}
  }, [collapsed]);

  const journeyButton = onOpenJourney ? (
    <button
      type="button"
      className="canvas-steps__journey"
      aria-label={STEPS.JOURNEY}
      title={STEPS.JOURNEY}
      aria-haspopup="dialog"
      onClick={onOpenJourney}
    >
      <CanvasIcon path={ICON_PATHS.journey} size={18} strokeWidth={2} />
    </button>
  ) : null;

  const whenLabels = { labels: STATES, conditionLabels: CONDITIONS };

  const renderStates = (node, active) => {
    const states = orderedStates(get(node, "data.screenStates"));
    if (!states.length) return { toggle: null, list: null };
    const name = node.data.name || STEPS.UNNAMED;
    const shown = isNil(openStates[node.id]) ? active : openStates[node.id];
    const currentStateId = active ? selectedStateId || null : undefined;
    const toggle = (
      <button
        type="button"
        className={classNames("canvas-steps__chevron", {
          "canvas-steps__chevron--open": shown,
        })}
        aria-expanded={shown}
        aria-label={t(
          shown
            ? "CANVAS_EDITOR.STEPS.HIDE_STATES"
            : "CANVAS_EDITOR.STEPS.SHOW_STATES",
          { name },
        )}
        onClick={() =>
          setOpenStates((current) => ({ ...current, [node.id]: !shown }))
        }
      >
        <CanvasIcon path={ICON_PATHS.chevronRight} size={16} strokeWidth={2} />
      </button>
    );
    const list = shown ? (
      <div className="canvas-steps__states">
        {[null, ...states].map((state) => {
          const stateId = state ? state.id : null;
          const current = currentStateId === stateId;
          return (
            <button
              key={stateId || "default"}
              type="button"
              className={classNames("canvas-steps__state", {
                "canvas-steps__state--active": current,
              })}
              aria-current={current ? "true" : undefined}
              onClick={() => onSelectState(node.id, stateId)}
            >
              <CanvasIcon path={ICON_PATHS.layers} size={14} strokeWidth={2} />
              <span className="canvas-steps__state-text">
                <span className="canvas-steps__state-name">
                  {state ? state.name || state.id : STATES.DEFAULT}
                </span>
                <span className="canvas-steps__state-when">
                  {stateWhen(state, variables, whenLabels)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    ) : null;
    return { toggle, list };
  };

  if (collapsed)
    return (
      <div className="canvas-steps canvas-steps--collapsed">
        <button
          type="button"
          className="canvas-steps__toggle"
          aria-label={STEPS.EXPAND}
          title={STEPS.EXPAND}
          aria-expanded="false"
          onClick={() => setCollapsed(false)}
        >
          <BiChevronsRight size={20} />
        </button>
        {journeyButton}
        <button
          type="button"
          className="canvas-steps__toggle"
          aria-label={STEPS.ADD}
          title={STEPS.ADD}
          onClick={onAdd}
        >
          <BiPlus size={20} />
        </button>
      </div>
    );

  return (
    <div className="canvas-steps">
      <div className="canvas-steps__header">
        <div className="canvas-steps__title">
          {STEPS.TITLE}
          <span className="canvas-steps__count">{nodes.length}</span>
          {journeyButton}
          <button
            type="button"
            className="canvas-steps__toggle"
            aria-label={STEPS.COLLAPSE}
            title={STEPS.COLLAPSE}
            aria-expanded="true"
            onClick={() => setCollapsed(true)}
          >
            <BiChevronsLeft size={20} />
          </button>
        </div>
        <label className="canvas-steps__search">
          <CanvasIcon path={ICON_PATHS.search} size={16} strokeWidth={2} />
          <input
            type="search"
            value={search}
            placeholder={STEPS.SEARCH}
            aria-label={STEPS.SEARCH}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>
      <nav className="canvas-steps__list" aria-label={STEPS.TITLE}>
        {visible.map(({ node, number, branch }) => {
          const active = node.id === selectedId;
          const tone = stepTone(node, nodes, checksById[node.id]);
          const look = stepLook(node.data.nodeType);
          const states = renderStates(node, active);
          return (
            <div key={node.id} className="canvas-steps__entry">
              <div
                className={classNames("canvas-steps__row", {
                  "canvas-steps__row--active": active,
                })}
              >
                <button
                  type="button"
                  className={classNames("canvas-steps__item", {
                    "canvas-steps__item--active": active,
                    "canvas-steps__item--branch": branch,
                  })}
                  aria-current={active ? "step" : undefined}
                  onClick={() => onSelect(node.id)}
                >
                  <span className="canvas-steps__number">
                    {number || STEPS.BRANCH}
                  </span>
                  <span
                    className={`canvas-steps__icon canvas-tone--${look.tone}`}
                  >
                    <CanvasIcon path={look.icon} />
                  </span>
                  <span className="canvas-steps__text">
                    <span className="canvas-steps__name">
                      {node.data.name || STEPS.UNNAMED}
                    </span>
                    <span className="canvas-steps__sub">
                      {describeStep(node, t)}
                    </span>
                  </span>
                  <span
                    className={`canvas-steps__dot canvas-steps__dot--${tone}`}
                  />
                </button>
                {states.toggle}
              </div>
              {states.list}
            </div>
          );
        })}
        {visible.length === 0 ? (
          <div className="canvas-steps__empty">{STEPS.NO_MATCH}</div>
        ) : null}
      </nav>
      <div className="canvas-steps__footer">
        <button type="button" className="canvas-steps__add" onClick={onAdd}>
          <CanvasIcon path={ICON_PATHS.plus} strokeWidth={2.2} />
          {STEPS.ADD}
        </button>
      </div>
    </div>
  );
};

StepList.propTypes = {
  nodes: PropTypes.arrayOf(PropTypes.object),
  edges: PropTypes.arrayOf(PropTypes.object),
  checksById: PropTypes.object,
  selectedId: PropTypes.string,
  selectedStateId: PropTypes.string,
  variables: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  onSelectState: PropTypes.func,
  onOpenJourney: PropTypes.func,
  onAdd: PropTypes.func.isRequired,
};

StepList.defaultProps = {
  nodes: [],
  edges: [],
  checksById: {},
  selectedId: null,
  selectedStateId: null,
  variables: [],
  onSelectState: () => undefined,
  onOpenJourney: null,
};

export default StepList;
